'use client';
import { removeFromLocalStorage } from '@/lib/local-storage/local-storage';
import { Urls } from '@/lib/urls';
import { redirect, usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useIsClient } from 'usehooks-ts';
import { useSession } from './index';

import { SessionLoader } from './session-loader';
import { SESSION_KEY } from './constants';
import { isAnonymousRoute, isLoginRoute } from './utils';

export function AuthGuard({
  protectedRoutes,
  anonymousRoutes,
}: {
  protectedRoutes: React.ReactNode;
  anonymousRoutes: React.ReactNode;
}) {
  const pathname = usePathname();
  const { status } = useSession({ required: false });

  const isClient = useIsClient();
  const isSpecialRoute = isAnonymousRoute(pathname) || isLoginRoute(pathname);

  const [isLoadingVisible, setIsLoadingVisible] = useState(true);
  const [shouldShowLoading, setShouldShowLoading] = useState(
    status === 'loading'
  );

  useEffect(() => {
    if (isSpecialRoute) {
      return;
    }
    if (status === 'unauthenticated') {
      removeFromLocalStorage({ key: SESSION_KEY });
      redirect(Urls.login.login);
    }
  }, [status, isSpecialRoute]);

  // Handle fade-out animation when loading completes
  useEffect(() => {
    if (status === 'loading') {
      setShouldShowLoading(true);
      setIsLoadingVisible(true);
    } else if (shouldShowLoading) {
      // Start fade-out
      setIsLoadingVisible(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldShowLoading(false);
      }, 500); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [status, shouldShowLoading]);

  if (!isClient) {
    return null;
  }

  if (isSpecialRoute) {
    return anonymousRoutes;
  }

  if (status === 'unauthenticated') {
    return null;
  }

  return (
    <>
      {status === 'authenticated' && protectedRoutes}
      {shouldShowLoading && <SessionLoader isVisible={isLoadingVisible} />}
    </>
  );
}
